import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ReportCard } from './ReportCard';
import { SuggestedActions } from './SuggestedActions';
import { AgentSparkleAvatar } from './v2/AgentSparkleAvatar';

const _sanitizeRows = (text) => (text || '').toString();

const fmtTs = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Inline-button replacement for any in-app deep link the agent emits. The
// agent sometimes generates phrasing like "Open Waiting for Approvals
// Queue" as an <a href="/payments/..."> — when clicked it leads to "page not
// found" because the queue lives INSIDE the AI Agent drawer (Email →
// Approvals tab), not on its own URL. This component intercepts the link
// and dispatches a global event the drawer listens to.
const _isApprovalsLink = ({ href, children }) => {
  const label = React.Children.toArray(children).map((c) => (typeof c === 'string' ? c : '')).join(' ').toLowerCase();
  const h = (href || '').toLowerCase();
  return label.includes('waiting for approval') ||
         label.includes('approvals queue') ||
         h.includes('approvals') || h.includes('pending_approval');
};

const InAppLink = ({ node, href, children, ...rest }) => {
  if (_isApprovalsLink({ href, children })) {
    return (
      <button
        type="button"
        data-testid="ai-msg-open-approvals"
        onClick={(e) => {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent('ai-agent:open', {
            detail: { tab: 'email', emailTab: 'approvals' },
          }));
        }}
        className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[12px] font-semibold text-amber-700 hover:bg-amber-200"
      >
        {children}
        <span aria-hidden="true">↗</span>
      </button>
    );
  }
  // External / internal page link — open in same tab if relative, new tab otherwise.
  const isExternal = /^https?:\/\//i.test(href || '');
  return (
    <a
      className="text-teal-700 underline"
      href={href}
      target={isExternal ? '_blank' : '_self'}
      rel={isExternal ? 'noreferrer' : undefined}
      {...rest}
    >
      {children}
    </a>
  );
};

export const MessageBubble = ({ message, onSuggestedAction, suggestedDisabled, agentName }) => {
  // tool messages — render a small chip in transcript so user sees what ran
  if (message.role === 'tool') {
    let summary = '';
    try {
      const parsed = JSON.parse(message.content || '{}');
      const ok = parsed.ok ? '✓' : '✗';
      const rows = parsed.row_count != null ? ` (${parsed.row_count} rows)` : '';
      const errCode = parsed.error_code ? ` — ${parsed.error_code}` : '';
      summary = `${ok} ${parsed.name || message.tool_name || 'tool'}${rows}${errCode}`;
    } catch {
      summary = message.tool_name || 'tool';
    }
    return (
      <div data-testid="ai-agent-tool-chip" className="my-1 text-[11px] text-slate-400 italic">
        {summary}
      </div>
    );
  }

  if (message.role === 'user') {
    return (
      <div data-testid="ai-agent-user-message" className="flex flex-col items-end my-2">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-slate-900 text-white px-4 py-2 text-sm leading-relaxed whitespace-pre-wrap">
          {_sanitizeRows(message.content)}
        </div>
        {message.ts && (
          <span className="mt-0.5 mr-1 text-[10px] text-slate-400" data-testid="ai-msg-user-ts">{fmtTs(message.ts)}</span>
        )}
      </div>
    );
  }

  // assistant
  const resolvedAgentName = agentName
    || (typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('ai_agent_persona_name') : null)
    || 'Astrid';
  return (
    <div data-testid="ai-agent-assistant-message" className="flex flex-col items-start my-2">
      <div className="flex items-start gap-2 max-w-[95%]">
        {/* Agent sparkle avatar — first letter of the agent's display name */}
        <div className="mt-0.5" data-testid="ai-msg-bp-avatar">
          <AgentSparkleAvatar name={resolvedAgentName} size={28} />
        </div>
        <div className="min-w-0 flex-1">
          {message.ts && (
            <div className="mb-0.5">
              <span className="text-[10px] text-slate-400" data-testid="ai-msg-asst-ts">{fmtTs(message.ts)}</span>
            </div>
          )}
          <div className="rounded-2xl rounded-tl-sm bg-slate-50 text-slate-900 px-4 py-3 text-sm leading-relaxed prose prose-sm max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto my-2">
                    <table className="w-full text-xs border-collapse" {...props} />
                  </div>
                ),
                th: ({ node, ...props }) => (
                  <th className="border-b border-slate-300 px-2 py-1 text-left font-semibold bg-slate-100" {...props} />
                ),
                td: ({ node, ...props }) => (
                  <td className="border-b border-slate-200 px-2 py-1 align-top" {...props} />
                ),
                a: InAppLink,
                code: ({ inline, ...props }) =>
                  inline
                    ? <code className="bg-slate-200 rounded px-1 py-0.5 text-[12px]" {...props} />
                    : <pre className="bg-slate-900 text-slate-50 rounded p-2 overflow-x-auto"><code {...props} /></pre>,
              }}
            >
              {_sanitizeRows(message.content)}
            </ReactMarkdown>
            {message.report_job && <ReportCard job={message.report_job} />}
            {message.suggested_actions?.length > 0 && (
              <SuggestedActions
                actions={message.suggested_actions}
                onPick={onSuggestedAction}
                disabled={suggestedDisabled}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
